import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { isSafeRelativeUrl } from '../utils/safeRedirect';
import { shouldIgnoreAuthSession, toEffectiveAuthSession } from '../utils/authRecovery';
import { readStoredAuthSession, resolveAuthSession } from '../lib/auth/storedSession';

const CurrentUserProfileContext = createContext(null);

/** Never leave authReady false indefinitely — queries and routes depend on it. */
const AUTH_SESSION_BOOT_TIMEOUT_MS = 2500;

/** In-memory cache for the signed-in user's profile row (same tab session). */
const profileCacheRef = { userId: null, profile: null, hasLoaded: false };

/**
 * On-device copy of your own name and photo, so a cold start with no service still shows who you are. Sign-out wipes
 * localStorage (Settings / Auth), which takes this with it.
 */
const PROFILE_STORAGE_PREFIX = 'prii:my-profile:';

function readStoredProfile(userId) {
  if (!userId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(`${PROFILE_STORAGE_PREFIX}${userId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStoredProfile(userId, profile) {
  if (!userId || !profile || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${PROFILE_STORAGE_PREFIX}${userId}`, JSON.stringify(profile));
  } catch {
    /* storage full or blocked: the in-memory copy still serves this run */
  }
}

/** Fetch your profile row; on a network failure keep the on-device copy instead of blanking it. */
async function loadProfile(userId) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('display_name, profile_photo_url')
      .eq('id', userId)
      .single();
    if (!error && data) {
      writeStoredProfile(userId, data);
      return data;
    }
    return readStoredProfile(userId);
  } catch {
    return readStoredProfile(userId);
  }
}

export function CurrentUserProfileProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  /** True once getSession() has resolved — session may still be null. */
  const [authReady, setAuthReady] = useState(false);

  const refetch = useCallback(async () => {
    if (!session?.user?.id) {
      profileCacheRef.userId = null;
      profileCacheRef.profile = null;
      profileCacheRef.hasLoaded = false;
      setProfile(null);
      return;
    }
    const next = (await loadProfile(session.user.id)) ?? null;
    profileCacheRef.userId = session.user.id;
    profileCacheRef.profile = next;
    profileCacheRef.hasLoaded = true;
    setProfile(next);
  }, [session?.user?.id]);

  useEffect(() => {
    let cancelled = false;
    let resolved = false;

    const finishBoot = (session) => {
      if (cancelled || resolved) return;
      resolved = true;
      const effectiveSession = toEffectiveAuthSession(session);
      setSession(effectiveSession);
      setAuthReady(true);
      if (effectiveSession?.user?.id) {
        const pendingInvite = sessionStorage.getItem('pendingInviteUrl');
        if (pendingInvite && isSafeRelativeUrl(pendingInvite)) {
          sessionStorage.removeItem('pendingInviteUrl');
          window.location.href = pendingInvite;
        } else if (pendingInvite) {
          sessionStorage.removeItem('pendingInviteUrl');
        }
      }
    };

    // Signed in = a stored session with a refresh token (lib/auth/storedSession). Boot on it now: with no service
    // getSession() is stuck on (or fails) the refresh of an expired token, and that used to sign you out on screen.
    const storedAtBoot = readStoredAuthSession();
    if (storedAtBoot) finishBoot(storedAtBoot);

    const bootTimeout = setTimeout(() => {
      if (resolved) return;
      const stored = readStoredAuthSession();
      console.warn(
        `[auth] session boot timed out; continuing ${stored ? 'with stored session' : 'without session'}`,
      );
      finishBoot(stored);
    }, AUTH_SESSION_BOOT_TIMEOUT_MS);

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        clearTimeout(bootTimeout);
        finishBoot(resolveAuthSession(session));
      })
      .catch((err) => {
        clearTimeout(bootTimeout);
        console.warn('[auth] getSession failed; continuing with stored session if any:', err);
        finishBoot(readStoredAuthSession());
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, rawSession) => {
      // Only SIGNED_OUT (or auth-js deleting the stored session after a real auth error) signs you out; a null
      // INITIAL_SESSION from an offline refresh keeps the stored session.
      const session = resolveAuthSession(rawSession, { event });
      if (shouldIgnoreAuthSession(session, { event })) {
        return;
      }
      setSession(session);
      setAuthReady(true);
      if (!session?.user?.id) {
        profileCacheRef.userId = null;
        profileCacheRef.profile = null;
        profileCacheRef.hasLoaded = false;
      }
      if (session?.user?.id) {
        const pendingInvite = sessionStorage.getItem('pendingInviteUrl');
        if (pendingInvite && isSafeRelativeUrl(pendingInvite)) {
          sessionStorage.removeItem('pendingInviteUrl');
          window.location.href = pendingInvite;
        } else if (pendingInvite) {
          sessionStorage.removeItem('pendingInviteUrl');
        }
      }
    });
    return () => {
      cancelled = true;
      clearTimeout(bootTimeout);
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session?.user?.id) {
      profileCacheRef.userId = null;
      profileCacheRef.profile = null;
      profileCacheRef.hasLoaded = false;
      setProfile(null);
      setLoading(false);
      return;
    }
    if (profileCacheRef.userId === session.user.id && profileCacheRef.hasLoaded) {
      setProfile(profileCacheRef.profile);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const storedProfile = readStoredProfile(session.user.id);
    if (storedProfile) {
      // Paint your name/photo from the phone at once; the fetch below refreshes it when there is service.
      setProfile(storedProfile);
      setLoading(false);
    } else {
      setLoading(true);
    }
    loadProfile(session.user.id)
      .then((data) => {
        if (!cancelled) {
          const next = data ?? null;
          profileCacheRef.userId = session.user.id;
          profileCacheRef.profile = next;
          profileCacheRef.hasLoaded = true;
          setProfile(next);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          profileCacheRef.userId = session.user.id;
          profileCacheRef.profile = null;
          profileCacheRef.hasLoaded = true;
          setProfile(null);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  const value = { profile, loading, authReady, refetch, session };

  return (
    <CurrentUserProfileContext.Provider value={value}>
      {children}
    </CurrentUserProfileContext.Provider>
  );
}

export function useCurrentUserProfile() {
  const ctx = useContext(CurrentUserProfileContext);
  if (!ctx) throw new Error('useCurrentUserProfile must be used within CurrentUserProfileProvider');
  return ctx;
}
